/**
 * src/shared/lib/syncProtocol.ts
 *
 * Pure sync-protocol decision helpers shared by the producer (client queue)
 * and the consumer (useSyncStore.flush()). CRITICAL: this module must stay
 * Worker-safe and browser-safe simultaneously.
 *
 * No zustand, no idb-keyval, no React, no browser-only globals (window,
 * navigator, fetch, localStorage, IndexedDB). Every export here is a plain,
 * total, side-effect-free function over plain data — testable without any
 * runtime environment.
 *
 * CR-01 (35-REVIEW.md): the server's POST /api/sync/batch response now
 * carries a `rejected` array of change ids it could not apply. The client
 * must dequeue ONLY the ids that were NOT rejected — dequeuing the whole
 * chunk on a bare 200 is the silent-drop bug this module closes.
 *
 * Phase 36 (SYNC-02): resolveEnqueueProjectId / buildRoomUpsertRecord /
 * buildRoomDeleteRecord are the producer-side decision — whether a room edit
 * should enter the queue at all, and what record to build when it should.
 * crypto.randomUUID() is a Worker-safe and browser-safe global (available in
 * both workerd and every supported browser), so it does not violate this
 * module's purity header.
 *
 * Plan 36-04: buildProjectItemsRecord / buildProjectMetaRecord extend the same
 * producer-side decision to the project-level GLOBAL layer (projectItems /
 * projectOverrides / safetyMargin, and the project name + ProjectMeta block).
 * Both are keyed by projectId only (no roomId) — see ChangeType's doc comment
 * in src/entities/sync/model/types.ts.
 */
import type { ChangeRecord } from '@/entities/sync/model/types';
import type { Room } from '@/entities/room/model/types';
import type { EstimateItem, ProjectMeta, ProjectSnapshot } from '@/entities/estimate/model/types';

/** Mirrors ProjectSnapshot['projectOverrides'] without redefining its fields (CLAUDE.md rule 7). */
type ProjectOverrides = NonNullable<ProjectSnapshot['projectOverrides']>;

/**
 * Given the ids sent in one flushed chunk and the server's `rejected` list
 * for that chunk's response, return the set of ids that were actually
 * accepted by the server and are therefore safe to dequeue.
 *
 * Total and defensive:
 *  - Empty `rejected` → every chunk id is flushed.
 *  - A `rejected` entry whose id is not present in `chunkIds` is ignored
 *    (it cannot affect what this chunk dequeues).
 *  - Duplicate ids in `chunkIds` collapse naturally (Set semantics).
 */
export function selectFlushedIds(
  chunkIds: string[],
  rejected: readonly { id: string }[],
): Set<string> {
  const rejectedIds = new Set(rejected.map((r) => r.id));
  const flushed = new Set<string>();
  for (const id of chunkIds) {
    if (!rejectedIds.has(id)) {
      flushed.add(id);
    }
  }
  return flushed;
}

/**
 * Decide whether a room-level edit should enter the sync queue at all, and if
 * so, which projectId the resulting ChangeRecord belongs to.
 *
 * Returns the projectId (coerced to a string) only when ALL of:
 *  - `userId` is a non-empty string (T-04-04 guest gate — a guest session
 *    must never accumulate queued change records that a later login would
 *    upload under a different identity),
 *  - `producerEnabled` is true (the SYNC_PRODUCER_ENABLED operator kill
 *    switch),
 *  - `activeProjectId` is non-null (a change record has nowhere to belong
 *    otherwise).
 * Returns null otherwise. A numeric `activeProjectId` is returned as its
 * string form.
 */
export function resolveEnqueueProjectId(
  userId: string | null | undefined,
  producerEnabled: boolean,
  activeProjectId: string | number | null,
): string | null {
  if (!userId || !producerEnabled || activeProjectId === null || activeProjectId === undefined) {
    return null;
  }
  return String(activeProjectId);
}

/**
 * Build an 'upsert_room' ChangeRecord for the given room.
 * `baseVersion` carries the room's server-assigned OCC token (room.lastModified),
 * or null when the room has never synced — the server treats null/absent as
 * "always apply" (see ChangeRecord.baseVersion doc in
 * src/entities/sync/model/types.ts).
 */
export function buildRoomUpsertRecord(projectId: string, room: Room): ChangeRecord {
  return {
    id: crypto.randomUUID(),
    projectId,
    roomId: room.id,
    type: 'upsert_room',
    payload: room,
    timestamp: new Date().toISOString(),
    schemaVersion: 1,
    baseVersion: room.lastModified ?? null,
  };
}

/**
 * Build a 'delete_room' ChangeRecord for the given roomId. Deletions carry no
 * payload and no OCC token — a delete always applies (there is nothing left
 * to conflict with).
 */
export function buildRoomDeleteRecord(projectId: string, roomId: string): ChangeRecord {
  return {
    id: crypto.randomUUID(),
    projectId,
    roomId,
    type: 'delete_room',
    payload: null,
    timestamp: new Date().toISOString(),
    schemaVersion: 1,
    baseVersion: null,
  };
}

/**
 * Build an 'upsert_project_items' ChangeRecord for the project's GLOBAL item layer
 * (Plan 36-04, SYNC-02). Whole-value-replacement semantics: the payload carries
 * exactly the three fields `ProjectItemsPayloadSchema` declares — projectItems,
 * projectOverrides, safetyMargin — and NOTHING else. In particular it must never
 * carry `meta`: that field rides `buildProjectMetaRecord` below so each field has
 * exactly one writer (36-02-SUMMARY.md Assumption A3).
 *
 * An empty `projectItems` array is a real, sync-worthy value (a user who deleted
 * every global item must have that deletion reach the server) — this builder
 * applies no truthiness guard, it simply forwards whatever `layer` carries.
 * No roomId — this record is keyed by projectId only. baseVersion is always
 * null: the global layer has no cross-batch OCC token (accepted limitation,
 * carried from 36-RESEARCH.md Open Question 2).
 */
export function buildProjectItemsRecord(
  projectId: string,
  layer: { projectItems: EstimateItem[]; projectOverrides: ProjectOverrides; safetyMargin: number },
): ChangeRecord {
  return {
    id: crypto.randomUUID(),
    projectId,
    type: 'upsert_project_items',
    payload: {
      projectItems: layer.projectItems,
      projectOverrides: layer.projectOverrides,
      safetyMargin: layer.safetyMargin,
    },
    timestamp: new Date().toISOString(),
    schemaVersion: 1,
    baseVersion: null,
  };
}

/**
 * Build an 'upsert_project_meta' ChangeRecord (Plan 36-04, SYNC-02) carrying the
 * project's top-level `name` (rename semantics, may be undefined when the active
 * project has no known library entry) and the full `ProjectMeta` (client/
 * contractor/VAT) block. No roomId, no baseVersion (same accepted limitation as
 * the item layer above) — keyed by projectId only.
 */
export function buildProjectMetaRecord(
  projectId: string,
  name: string | undefined,
  meta: ProjectMeta,
): ChangeRecord {
  return {
    id: crypto.randomUUID(),
    projectId,
    type: 'upsert_project_meta',
    payload: { name, meta },
    timestamp: new Date().toISOString(),
    schemaVersion: 1,
    baseVersion: null,
  };
}
